import Uppy from '@uppy/core';

import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css'; // Use this if you need dashboard styles in Uppy v5+
import CustomUppyUpload from '@uppy/react/dashboard';
import AwsS3 from '@uppy/aws-s3';
import OneDrive from '@uppy/onedrive';
import GoogleDrivePicker from '@uppy/google-drive-picker';
import { useThemeProps, styled } from '@mui/material/styles';
import { forwardRef, useEffect, useMemo, useState, useImperativeHandle } from 'react';
import pLimit from 'p-limit';
import { DocumentSource } from '../../types/Documents';
import { useCreateDocument } from '../../hooks/useCreateDocument';
import { useCreatePipelineRun } from '../../hooks/useCreatePipelineRun';
import { context } from '../../apollo/context';
/**
 * Props for the UppyUpload component.
 *
 * @typedef {Object} UppyUploadProps
 * @property {string} companionEndpoint - The endpoint for the Uppy companion server.
 * @property {string} authToken - The authentication token for the companion server.
 * @property {string} [caseId] - The case ID for the document.
 * @property {string} [orgId] - The organization ID for the document.
 * @property {function} [onFilesChange] - Callback function when files are added or removed.
 * @property {function} [onUploadSuccess] - Callback function when a file is successfully uploaded.
 * @property {boolean} [startUpload] - Flag to start the upload process.
 */
export interface UppyUploadProps {
  companionEndpoint: string;
  authToken: string;
  onFilesChange?: (files: any[]) => void;
  onUploadSuccess?: () => void;
  documentSource?: DocumentSource;
  pipelineType: string;
  disabled?: boolean;
  fileLimit?: number;
}

const CustomUppyUploadRoot = styled('div', {
  name: 'CustomUppyUpload',
  slot: 'root',
})(({ theme }) => ({
  '*': {
    fontFamily: 'Geist,Arial,sans-serif !important',
    fontColor: theme.palette.secondary.contrastText,
  },
  '.uppy-Dashboard-inner': {
    maxHeight: '220px',
    backgroundColor: theme.palette.info.light,
    borderRadius: 0,
    border: `1px dashed ${theme.palette.info.dark}`,
    width: '100% !important',
  },
  '.uppy-DashboardContent-panel': {
    borderRadius: '16px',
  },
  '.uppy-DashboardContent-bar': {
    height: '30px',
    borderBottom: `1px dashed ${theme.palette.text.disabled}`,
    backgroundColor: 'transparent',
  },
  '.uppy-DashboardContent-title': {
    lineHeight: '30px',
  },
  '.uppy-ProviderBrowser-headerBar': {
    padding: '0 15px',
  },
  '.uppy-Dashboard-AddFiles': {
    border: 0,
  },
  '.uppy-Dashboard-AddFiles-title': {
    fontSize: '14px',
  },
  '.uppy-StatusBar': {
    display: 'none',
  },
  '.uppy-ProviderBrowser-footer': {
    padding: '4px 15px',
  },
  '.uppy-ProviderBrowser-searchFilter': {
    marginTop: '4px',
    marginBottom: '4px',
  },
  '.uppy-ProviderBrowserItem': {
    padding: '3px 15px !important',
  },
  '.uppy-c-btn-primary': {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
  },
  '.uppy-Provider-authTitle': {
    fontSize: '1rem',
  },
  '&.is-disabled': {
    pointerEvents: 'none',
    opacity: 0.6,
  },
  '&.is-disabled .uppy-c-btn, &.is-disabled button, &.is-disabled input': {
    pointerEvents: 'none',
  },
}));

/**
 * UppyUpload Component
 *
 * This component is a wrapper around the Uppy file uploader, configured to handle specific file types and sizes.
 * It integrates with Apollo Client to create documents in the backend and supports various cloud storage services.
 *
 * @component
 * @param {UppyUploadProps} inProps - The properties passed to the component.
 * @param {React.Ref<HTMLDivElement>} ref - The ref to the root div element.
 *
 * @returns {JSX.Element} The rendered UppyUpload component.
 *
 * @example
 * <UppyUpload
 *   companionEndpoint="https://companion.example.com"
 *   authToken="your-auth-token"
 *   caseId="case-id"
 *   orgId="organization-id"
 *   onFilesChange={handleFilesChange}
 *   onUploadSuccess={handleUploadSuccess}
 *   startUpload={startUpload}
 * />
 */
const UppyUpload = forwardRef<any, UppyUploadProps>(function UppyUpload(inProps, ref) {
  const { createDocument } = useCreateDocument();
  const { createPipelineRun } = useCreatePipelineRun();
  const props = useThemeProps({ props: inProps, name: 'CustomUppyUpload' });
  const {
    companionEndpoint,
    authToken,
    onFilesChange,
    onUploadSuccess,
    documentSource,
    pipelineType,
    disabled,
    fileLimit = 1000,
    ...other
  } = props;

  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Create Uppy instance
  const uppy = useMemo(() => {
    const instance = new Uppy({
      restrictions: {
        maxNumberOfFiles: fileLimit,
        allowedFileTypes: [
          'application/pdf',
          'text/plain',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        ],
        maxFileSize: 200 * 1024 * 1024,
      },
      locale: {
        strings: {
          youCanOnlyUploadFileTypes: 'Only PDF, PPTX, Word and Txt file formats will be uploaded',
          browseFiles: 'Browse',
          dropPasteImportFiles: 'Drag & Drop your files or %{browseFiles}',
        },
        pluralize: (n: number) => (n === 1 ? 0 : 1),
      },
      autoProceed: false,
    }).use(OneDrive, { companionUrl: companionEndpoint });

    const hasGoogleDriveCreds =
      !!context.googleDriveClientId && !!context.googleDriveApiKey && !!context.googleDriveAppId;

    if (hasGoogleDriveCreds) {
      instance.use(GoogleDrivePicker, {
        companionUrl: companionEndpoint,
        clientId: context.googleDriveClientId ?? '',
        apiKey: context.googleDriveApiKey ?? '',
        appId: context.googleDriveAppId ?? '',
      });
    }

    return instance;
  }, [
    companionEndpoint,
    fileLimit,
    context.googleDriveClientId,
    context.googleDriveApiKey,
    context.googleDriveAppId,
  ]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    getFiles: () => uppy.getFiles(),
    startCoordinatedUpload: async (caseId: string, orgId: string) => {
      if (uploadedFiles.length === 0 || isUploading) return;

      setIsUploading(true);

      try {
        // Configure AWS S3 for upload
        if (!uppy.getPlugin('AwsS3')) {
          uppy.use(AwsS3, {
            endpoint: companionEndpoint,
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
            allowedMetaFields: true,
          });
        }

        // Create documents with retry logic
        const limit = pLimit(20);

        const createAllDocuments = async (file: any) => {
          try {
            const result = await createDocument({
              case_id: caseId,
              filename: file.name,
            });
            return {
              fileId: file.id,
              fileName: file.name,
              documentData: result?.data?.createDocument,
            };
          } catch (error) {
            console.error(`Error creating document for file ${file.name}:`, error);
            throw error;
          }
        };

        // Create documents with concurrency limit and retry
        const results = await Promise.all(
          uploadedFiles.map(file => limit(() => createAllDocuments(file)))
        );
        //create pipeline run
        createPipelineRun({
          case_id: caseId,
          pipeline_type: pipelineType,
          documents: results
            .filter(({ documentData }) => documentData?.id)
            .map(({ documentData }) => documentData!.id),
        });

        // Set metadata for all files
        results.forEach(({ fileId, fileName, documentData }) => {
          const currFile = uppy.getFile(fileId);
          const relativePath = currFile?.meta?.relativePath;
          const docData = documentData as any; // Type assertion for metadata access
          uppy.setFileMeta(fileId, {
            filename: fileName,
            org_id: orgId,
            document_id: documentData?.id,
            case_id: caseId,
            path: docData?.metadata?.path || '',
            original_path: relativePath ?? '',
            document_source: documentSource,
          });
        });

        // Upload to S3 without retry logic
        const upload = async () => {
          const result = await uppy.upload();

          // Check for upload failures
          if ((result?.failed?.length ?? 0) > 0) {
            console.error('Some uploads failed:', result?.failed);

            // Log failed uploads, but do not throw or retry
            result?.failed?.forEach(file => {
              console.error(`Failed to upload ${file.name}:`, file.error);
            });
          }

          return result;
        };

        const result = await upload();

        // Remove AWS S3 plugin after upload
        const awsS3 = uppy.getPlugin('AwsS3');
        if (awsS3) {
          uppy.removePlugin(awsS3);
        }
      } catch (error) {
        console.error('Upload preparation failed after all retries:', error);

        // You might want to show a user-friendly error message here
        // or call an error callback if you add one to the props

        throw error; // Re-throw to let parent component handle it
      } finally {
        setIsUploading(false);
      }
    },
  }));

  // Handle basic file events
  useEffect(() => {
    const handleFileAdded = (file: any) => {
      setUploadedFiles(prev => [...prev, file]);
      onFilesChange?.([...uploadedFiles, file]);
    };

    const handleFileRemoved = (file: any) => {
      setUploadedFiles(prev => prev.filter(f => f.id !== file.id));
      onFilesChange?.(uploadedFiles.filter(f => f.id !== file.id));
    };

    uppy.on('file-added', handleFileAdded);
    uppy.on('file-removed', handleFileRemoved);
    uppy.on('complete', () => {
      onUploadSuccess?.();
    });

    return () => {
      uppy.off('file-added', handleFileAdded);
      uppy.off('file-removed', handleFileRemoved);
    };
  }, [uppy, onFilesChange, uploadedFiles]);

  // Cleanup
  useEffect(() => {
    return () => {
      uppy.cancelAll();
    };
  }, [uppy]);

  return (
    <CustomUppyUploadRoot
      ref={ref}
      {...other}
      className={disabled ? 'is-disabled' : undefined}
      aria-disabled={disabled ? true : undefined}
      style={{
        position: 'relative',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <CustomUppyUpload uppy={uppy} proudlyDisplayPoweredByUppy={false} />
    </CustomUppyUploadRoot>
  );
});

export default UppyUpload;
